/**
 * SHARED PIECES OF THE REFERRAL-AGENT AREA (2026-11-01).
 *
 * The agent area is six pages, so the money formatting, the tone palette and the fetch wrapper live here
 * rather than being copied six times. Money still arrives from the API in CENTS and is formatted only
 * here, so there is one rounding rule across every page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getApiBaseUrl, getAuthHeaders } from "../../utils/apiHelpers";

export const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

export const formatMonth = (label) => {
  if (!label) return "—";
  const [year, month] = String(label).split("-").map(Number);
  if (!year || !month) return label;
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
};

export const subscriptionTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
      return { background: "#ecfdf5", color: "#065f46", label: "Active" };
    case "trial":
    case "trialing":
      return { background: "#eff6ff", color: "#1d4ed8", label: "On trial" };
    case "past_due":
      return { background: "#fffbeb", color: "#92400e", label: "Payment due" };
    case "canceled":
    case "unpaid":
      return { background: "#fef2f2", color: "#991b1b", label: "Lapsed" };
    default:
      return { background: "#f1f5f9", color: "#334155", label: "—" };
  }
};

export const payoutTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "paid_out":
      return { background: "#ecfdf5", color: "#065f46", label: "Paid" };
    case "transfer_pending":
      return { background: "#eff6ff", color: "#1d4ed8", label: "On the way" };
    case "pending":
      return { background: "#fffbeb", color: "#92400e", label: "Queued" };
    case "failed":
      return { background: "#fef2f2", color: "#991b1b", label: "Failed" };
    case "canceled":
      return { background: "#f1f5f9", color: "#334155", label: "Cancelled" };
    case "paid":
      return { background: "#ecfdf5", color: "#065f46", label: "Paid" };
    case "accrued":
      return { background: "#fffbeb", color: "#92400e", label: "Accrued" };
    case "reversed":
      return { background: "#fef2f2", color: "#991b1b", label: "Reversed" };
    default:
      return { background: "#f1f5f9", color: "#334155", label: "—" };
  }
};

export const styles = {
  page: {
    maxWidth: "1080px",
    margin: "0 auto",
    padding: "24px 20px 56px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: "#0f172a",
  },
  h1: { fontSize: "22px", margin: "0 0 4px" },
  h2: { fontSize: "16px", margin: "0 0 12px" },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "16px",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "12px",
    marginBottom: "16px",
  },
  stat: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "14px 16px",
  },
  statLabel: {
    fontSize: "11px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    fontWeight: 600,
    display: "block",
    marginBottom: "6px",
  },
  statValue: { fontSize: "22px", fontWeight: 700, lineHeight: 1.2 },
  muted: { color: "#64748b", fontSize: "13px" },
  err: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: "10px",
    padding: "12px 14px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  notice: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1e3a8a",
    borderRadius: "10px",
    padding: "12px 14px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  btn: {
    border: "1px solid #1d4ed8",
    background: "#1d4ed8",
    color: "#ffffff",
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  btnGhost: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
  th: {
    textAlign: "left",
    fontSize: "11px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    fontWeight: 600,
    padding: "0 10px 10px 0",
    borderBottom: "1px solid #e2e8f0",
  },
  td: {
    padding: "12px 10px 12px 0",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
  },
  badge: {
    display: "inline-block",
    borderRadius: "999px",
    padding: "2px 9px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.02em",
  },
  empty: {
    padding: "28px 16px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "14px",
    minWidth: "220px",
  },
};

export function Badge({ tone }) {
  return (
    <span
      style={{ ...styles.badge, background: tone.background, color: tone.color }}
    >
      {tone.label}
    </span>
  );
}

/**
 * One GET against the agent API, with the "feature switched off" rule the original page used: a 404 means
 * the programme is not released, which is a calm state rather than a failure.
 */
export function useAgentResource(path) {
  const [state, setState] = useState({
    loading: true,
    error: "",
    unavailable: false,
    data: null,
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${getApiBaseUrl()}${path}`, { headers });
      if (!mounted.current) return;
      setState({ loading: false, error: "", unavailable: false, data: res.data });
    } catch (err) {
      if (!mounted.current) return;
      if (err?.response?.status === 404) {
        setState({ loading: false, error: "", unavailable: true, data: null });
      } else {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            err?.response?.data?.message ||
            "Unable to load this page right now.",
        }));
      }
    }
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}

export function AgentPageState({ loading, error, unavailable, reload, children }) {
  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.muted}>Loading…</p>
      </div>
    );
  }
  if (unavailable) {
    return (
      <div style={styles.page}>
        <div style={styles.card} data-testid="agent-unavailable">
          <p style={{ marginTop: 0 }}>The referral programme isn’t available yet.</p>
          <p style={{ ...styles.muted, marginBottom: 0 }}>
            This programme is being prepared. Check back soon.
          </p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.err}>{error}</div>
        <button type="button" style={styles.btn} onClick={reload}>
          Try again
        </button>
      </div>
    );
  }
  return children;
}
